import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ShowComponent } from '@/common/component/show.component';

import { TouristGuide, TouristGuideBankAccount, TouristGuideBankAccountType } from './tourist-guides.model';

@Component({
	selector: 'app-tourist-guides',
	templateUrl: './tourist-guides.component.html',
	styleUrls: ['./tourist-guides.component.css'],
})
export class TouristGuidesComponent extends ShowComponent implements OnInit {
	data: TouristGuide = {
		id: null,
		firstName: null,
		lastName: null,
		birthdate: null,
		phonePrivate: null,
		phoneBusiness: null,
		emailPrivate: null,
		emailBusiness: null,
		nearest_city: null,
		chronically_ill: 0,
		type: null,
		rate: null,
		comments: null,
		salaryComments: null,
		userId: null,
		bankAccounts: [],
	};
	errors = {};
	cities: Array<any> = [];
	types = [];
	users: Array<any> = [];
	filteredUsers: Array<any> = [];
	userControl = new FormControl<any>(null);
	readonly bankAccountTypes: Array<{ value: TouristGuideBankAccountType; label: string }> = [
		{ value: 'salary', label: 'Wynagrodzenie' },
		{ value: 'bonus', label: 'Dodatek' },
		{ value: 'other', label: 'Inne' },
	];
	apiUrlAdd = '/symfony/tourist-guide';
	selectedTabIndex = 0;
	protected itemsBeforeDataLoad = 3;
	protected parentComponent = 'touristGuides';
	private readonly tabNames = ['basic', 'rates', 'bank-accounts', 'salary', 'integration'];

	get isSalaryTab(): boolean {
		return this.tabNames[this.selectedTabIndex] === 'salary';
	}

	getApiUrl(): string {
		return '/symfony/tourist-guide/' + this.data.id;
	}

	protected override afterLoadData(): void {
		if (!Array.isArray(this.data.bankAccounts)) {
			this.data.bankAccounts = [];
		}
		this.filteredUsers = [...this.users];
		if (this.data.userId) {
			const found = this.users.find((u) => u.id === this.data.userId);
			if (found) this.userControl.setValue(found, { emitEvent: false });
		}
		if (this.pageFunction !== 'add' && !this.editable) {
			this.userControl.disable();
		}
		this.userControl.valueChanges.subscribe((val) => {
			if (typeof val === 'string') {
				const q = val.toLowerCase();
				this.filteredUsers = this.users.filter((u) =>
					`${u.firstName} ${u.lastName} ${u.email}`.toLowerCase().includes(q),
				);
				if (!val) this.data.userId = null;
			}
		});
	}

	override editUnlock(): void {
		super.editUnlock();
		this.userControl.enable();
	}

	displayUser = (user: any): string => {
		if (!user) return '';
		return `${user.firstName} ${user.lastName} | ${user.email}`;
	};

	onUserSelected(user: any): void {
		this.data.userId = user?.id ?? null;
	}

	get canAddBankAccount(): boolean {
		return this.data.bankAccounts.length < this.bankAccountTypes.length;
	}

	addBankAccount(): void {
		if (this.pageFunction === 'edit' && !this.editable) {
			this.editUnlock();
		}
		const usedTypes = this.data.bankAccounts.map((account) => account.type);
		const nextType = this.bankAccountTypes.find((type) => !usedTypes.includes(type.value))?.value;
		if (!nextType) {
			return;
		}
		this.data.bankAccounts.push({
			accountNumber: null,
			type: nextType,
			sameAsSalaryAccount: false,
		});
	}

	removeBankAccount(index: number): void {
		this.data.bankAccounts.splice(index, 1);
	}

	isBankAccountTypeTaken(type: TouristGuideBankAccountType, account: TouristGuideBankAccount): boolean {
		return this.data.bankAccounts.some((other) => other !== account && other.type === type);
	}

	onBankAccountTypeChange(account: TouristGuideBankAccount): void {
		if (account.type === 'salary') {
			account.sameAsSalaryAccount = false;
		}
	}

	onSameAsSalaryChange(account: TouristGuideBankAccount): void {
		if (account.sameAsSalaryAccount) {
			account.accountNumber = null;
		}
	}

	protected override doAdd(): void {
		this.api.post(this.apiUrlAdd, this.submitConvert(this.data), (result, error) => {
			if (!error) {
				this.data.id = result.data.id;
			}
			this.afterSend(result, error);
		});
	}

	protected override submitConvert(data: TouristGuide): any {
		return {
			...data,
			bankAccounts: (data.bankAccounts ?? []).map((account) => ({
				accountNumber: account.sameAsSalaryAccount ? null : account.accountNumber,
				type: account.type,
				sameAsSalaryAccount: account.sameAsSalaryAccount,
			})),
		};
	}

	override ngOnInit(): void {
		super.ngOnInit();

		this.route.queryParams.subscribe((params) => {
			const tabParam = params['tab'];
			if (tabParam) {
				const tabIndex = this.tabNames.indexOf(tabParam);
				if (tabIndex !== -1) {
					this.selectedTabIndex = tabIndex;
				}
			}
		});
	}

	onTabChange(index: number): void {
		this.selectedTabIndex = index;
		this.router.navigate([], {
			relativeTo: this.route,
			queryParams: { tab: this.tabNames[index] },
			queryParamsHandling: 'merge',
		});
	}

	protected loadBefore() {
		this.api.get('/nearestBigCity/', (result, error) => {
			this.cities = result.data;
			this.loadData();
		});

		this.api.get('/touristGuideTypes/', (result, error) => {
			this.types = result.data;
			this.loadData();
		});

		this.api.get('/users/', (result, _error) => {
			this.users = result.data;
			this.loadData();
		});
	}

	protected pageTitleGenerate(): string {
		return 'Edycja: ' + this.data.firstName + ' ' + this.data.lastName;
	}
}
